import React from 'react';

import Button from '../../../../library/button/button';
import MinimumPriceMessage from '../../../../components/minimum-price-message/minimum-price-message';
import Price from '../../../../components/price/price';
import ProductDescription from '../../../../components/product-description/product-description';
import ProductFlags from '../../../../components/product-flags/product-flags';
import ProductImage from '../../../../components/product-image/product-image';
import ProductInstantPromoMessage from '../../../../components/product-instant-promo-message/product-instant-promo-message';
import StackedMarkdownPrice from '../../../../components/stacked-markdown-price/stacked-markdown-price';
import WinterTireBadge from '../../../../components/winter-tire-badge/winter-tire-badge';
import { TimesRegularIcon } from '../../../../shared/icons/icons';

import {
  useMediaQuery,
  useMyAccount,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import {
  getIsNoLongerAvailable,
  getPriceInfo,
  getPrioritizedArticleStatusProduct,
  getShouldShowInstantPromotionMessage,
  getShouldShowMinimumPriceMessage,
  getStrikethroughInstantSavingsInfo,
  isWinterTire
} from '../../../../shared/utils/product/product';

import { ProductData } from '../../../../shared/types/graphql-types';
import { ProductQuantity as ProductQuantityType } from '../../../../shared/types/product';

import './product-compare-preview-summary.scss';

interface ProductComparePreviewSummaryProps {
  className?: string;
  hasInstantPromotions?: boolean;
  onClearProduct?: (product: ProductData) => void;
  product: ProductData;
  productQuantityData?: ProductQuantityType;
}

function ProductComparePreviewSummary(
  props: ProductComparePreviewSummaryProps
) {
  const {
    className,
    hasInstantPromotions,
    onClearProduct,
    product,
    productQuantityData
  } = props;
  const { totalQuantity } = productQuantityData || {};
  const { isLoggedIn } = useMyAccount();
  const { isMinMd } = useMediaQuery();
  const { isTreadwellFlow, treadwellPreferences } = useTreadwellPreferences();

  const isStaggered = Boolean(product?.rearProduct);
  const isWinterOnly = isWinterTire(product);
  const priceInfo = getPriceInfo(product);
  const rearPriceInfo = getPriceInfo(product?.rearProduct);
  const articleStatusProduct = getPrioritizedArticleStatusProduct(
    product,
    product?.rearProduct
  );
  const isNoLongerAvailable = getIsNoLongerAvailable(articleStatusProduct);

  const {
    instantStrikethroughSavingsPerProduct = 0,
    totalStrikethroughSavings = 0
  } = getStrikethroughInstantSavingsInfo(product, isLoggedIn, totalQuantity);
  const showInstantMinimumPriceMessage =
    process.env.__FF_INSTANT_PRICE_STRIKE_THROUGH__ === 'true' &&
    instantStrikethroughSavingsPerProduct
      ? !isLoggedIn
      : true;
  const showMinimumPriceMessage =
    showInstantMinimumPriceMessage && getShouldShowMinimumPriceMessage(product);
  const showRearMinimumPriceMessage =
    showInstantMinimumPriceMessage &&
    getShouldShowMinimumPriceMessage(product?.rearProduct);
  const showInstantPromotionMessage = getShouldShowInstantPromotionMessage(
    instantStrikethroughSavingsPerProduct,
    totalQuantity
  );
  const priceContainerStyleName = `${
    isStaggered ? 'price-container-staggered' : 'price-container'
  } ${
    hasInstantPromotions && !showInstantPromotionMessage
      ? 'price-container-no-promo'
      : ''
  }`;
  const isWinterTreadwellFlow = Boolean(treadwellPreferences?.showWinterTires);

  return (
    <div
      className={`${className || ''} product-compare-preview-summary`}
      styleName="container"
    >
      {product && (
        <div>
          <div styleName="flag-container">
            <div>
              <ProductFlags
                hideLabel
                hideTreadwellRank={!isTreadwellFlow}
                product={product}
              />
            </div>

            {isWinterTreadwellFlow && (
              <div styleName="winter-tire-badge">
                <WinterTireBadge isWinterOnly={isWinterOnly} />
              </div>
            )}
          </div>

          <Button
            icon={TimesRegularIcon}
            onClick={() => onClearProduct?.(product)}
            styleName="delete-button"
          />
        </div>
      )}
      {product && (
        <div>
          <ProductImage
            height={isMinMd ? 72 : 40}
            imageFormatType={
              process.env.__FF_PRODUCT_IMAGE_FORMATS_AUTO_SYNC__ === 'true'
                ? 'PRODUCT_X_SMALL'
                : 'PRODUCT'
            }
            product={product}
            styleName="product-image"
            width={isMinMd ? 72 : 40}
          />

          <div styleName="description">
            <ProductDescription
              brand={product?.brand}
              name={product?.name}
              styleName="product-description"
            />
            {!isNoLongerAvailable && (
              <div styleName={priceContainerStyleName}>
                <div styleName="price-info">
                  <StackedMarkdownPrice
                    hideInstantPromo={isStaggered}
                    product={product}
                    totalQuantity={totalQuantity}
                  />
                  <div styleName="price-item">
                    {isStaggered && <div styleName="price-label">F:&nbsp;</div>}

                    <Price
                      {...priceInfo}
                      isPriceForEach
                      seePriceInCartMessage="Price in cart"
                      totalQuantity={totalQuantity}
                      unitName={product?.unitName}
                    />
                  </div>
                </div>
                {isStaggered && (
                  <div styleName="price-info">
                    <StackedMarkdownPrice
                      hideInstantPromo
                      product={product?.rearProduct}
                      totalQuantity={totalQuantity}
                    />
                    <div styleName="price-item">
                      <div styleName="price-label">R:&nbsp;</div>

                      <Price
                        {...rearPriceInfo}
                        isPriceForEach
                        seePriceInCartMessage="Price in cart"
                        totalQuantity={totalQuantity}
                        unitName={product?.unitName}
                      />
                    </div>
                  </div>
                )}

                {((showMinimumPriceMessage &&
                  !priceInfo?.instantPromotionOriginalRetailValue) ||
                  showRearMinimumPriceMessage) && (
                  <MinimumPriceMessage
                    message={
                      product?.mapDisplayRule?.message ||
                      product.rearProduct?.mapDisplayRule?.message
                    }
                  />
                )}

                {showInstantPromotionMessage && isStaggered && (
                  <ProductInstantPromoMessage
                    instantStrikethroughSavingsPerProduct={
                      instantStrikethroughSavingsPerProduct
                    }
                    showShortDescription
                    totalStrikethroughSavings={totalStrikethroughSavings}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductComparePreviewSummary;
